import type { IconDefinition } from '@fortawesome/pro-light-svg-icons';
import type { IOmit } from '@sixui/ui';
import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import {
  faColumns3,
  faGear,
  faImages,
  faLanguage,
  faMobile,
  faPalette,
  faRadiation,
  faReceipt,
  faScaleBalanced,
  faTextSize,
  faUserGroup,
  faVideo,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, Skeleton, Stack } from '@sixui/ui';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';

import type { IAppSettingCardProps } from '~/components/molecules/AppSettingCard';
import type { IContainerProps } from '~/components/utils/Container';
import type { IFeaturePlanFeatures } from '~/lib/api/modules/Apps';
import type { IAppRegion } from '~/lib/appSettings';
import { AppSettingCard } from '~/components/molecules/AppSettingCard';
import { useAppRegion } from '~/hooks/useAppRegion';
import { useAppCurrentFeaturePlanQuery } from '~/lib/api/modules/Apps/hooks/useAppCurrentFeaturePlanQuery';
import { useUserQuery } from '~/lib/api/modules/Users/hooks/useUserQuery';

export type IAppSettingsProps = IContainerProps & {
  appId: string;
};

const styles = stylex.create({
  host: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 16,
  },
});

type ILinkItem = IOmit<IAppSettingCardProps, 'icon'> & {
  faIcon: IconDefinition;
  proFeature?: boolean;
};

type IGetItemsProps = {
  baseUrl: string;
  appId: string;
  appRegion: IAppRegion;
  t: TFunction<'translation', undefined>;
  features?: IFeaturePlanFeatures;
};

const getItems = ({
  baseUrl,
  appId,
  appRegion,
  t,
  features,
}: IGetItemsProps): Array<ILinkItem> => [
  {
    title: t('AppSettings.cards.general.title'),
    supportingText: t('AppSettings.cards.general.supportingText'),
    faIcon: faGear,
    href: `${baseUrl}/settings/general`,
  },
  {
    title: t('AppSettings.cards.lookAndFeel.title'),
    supportingText: t('AppSettings.cards.lookAndFeel.supportingText'),
    faIcon: faPalette,
    href: `${baseUrl}/settings/lookandfeel`,
    proFeature: !features?.appTheme,
  },
  {
    title: t('AppSettings.cards.tabs.title'),
    supportingText: t('AppSettings.cards.tabs.supportingText'),
    faIcon: faColumns3,
    href: `${baseUrl}/settings/tabs`,
    proFeature: !features?.appTabs,
  },
  {
    title: t('AppSettings.cards.formats.title'),
    supportingText: t('AppSettings.cards.formats.supportingText'),
    faIcon: faTextSize,
    href: `${baseUrl}/settings/formats`,
  },
  {
    title: t('AppSettings.cards.resources.title'),
    supportingText: t('AppSettings.cards.resources.supportingText'),
    faIcon: faImages,
    href: `${baseUrl}/settings/assets`,
  },
  {
    title: t('AppSettings.cards.users.title'),
    supportingText: t('AppSettings.cards.users.supportingText'),
    faIcon: faUserGroup,
    href: `${baseUrl}/settings/users`,
  },
  {
    title: t('AppSettings.cards.legalDocuments.title'),
    supportingText: t('AppSettings.cards.legalDocuments.supportingText'),
    faIcon: faScaleBalanced,
    href: `${baseUrl}/settings/legalDocuments`,
  },
  {
    title: t('AppSettings.cards.preview.title'),
    supportingText: t('AppSettings.cards.preview.supportingText'),
    faIcon: faMobile,
    href: `/preview/${appId}.${appRegion}`,
  },
  {
    title: t('AppSettings.cards.advanced.title'),
    supportingText: t('AppSettings.cards.advanced.supportingText'),
    faIcon: faRadiation,
    href: `${baseUrl}/settings/advanced`,
  },
  {
    title: t('AppSettings.cards.subscription.title'),
    supportingText: t('AppSettings.cards.subscription.supportingText'),
    faIcon: faReceipt,
    href: `${baseUrl}/settings/subscription`,
  },
  {
    title: t('AppSettings.cards.startupScreen.title'),
    supportingText: t('AppSettings.cards.startupScreen.supportingText'),
    faIcon: faVideo,
    href: `${baseUrl}/settings/startupScreen`,
    proFeature: !features?.startupScreen,
  },
  {
    title: t('AppSettings.cards.translations.title'),
    supportingText: t('AppSettings.cards.translations.supportingText'),
    faIcon: faLanguage,
    href: `${baseUrl}/settings/translations`,
    disabled: true,
    proFeature: !features?.translations,
  },
];

const renderItem = (item: ILinkItem, isSuperAdmin?: boolean): JSX.Element => {
  const { faIcon, title, proFeature, ...other } = item;

  return (
    <AppSettingCard
      icon={<FontAwesomeIcon icon={faIcon} />}
      title={
        proFeature ? (
          <Stack horizontal gap={2}>
            {title} <Badge value="Pro" />
          </Stack>
        ) : (
          title
        )
      }
      disabled={proFeature && !isSuperAdmin}
      {...other}
    />
  );
};

export const AppSettings: React.FC<IAppSettingsProps> = (props) => {
  const { sx, appId } = props;
  const { t } = useTranslation();
  const userQuery = useUserQuery({});
  const isSuperAdmin = userQuery.data?.superAdmin;

  const currentFeaturePlanQuery = useAppCurrentFeaturePlanQuery({
    appId,
  });
  const features = currentFeaturePlanQuery.data?.features;
  const appRegion = useAppRegion();
  const baseUrl = `/apps/${appId}`;
  const linkItems = useMemo(
    () => getItems({ baseUrl, appId, appRegion, t, features }),
    [baseUrl, appId, appRegion, t, features],
  );

  return (
    <div {...stylex.props(styles.host, sx)}>
      {linkItems.map((item, itemIndex) => (
        <Skeleton key={itemIndex} loaded={!currentFeaturePlanQuery.isLoading}>
          {renderItem(item, isSuperAdmin)}
        </Skeleton>
      ))}
    </div>
  );
};
